import axios, {AxiosResponse} from 'axios'
import type {LocationModel, PlaceInfoModel, PredictionsModel} from '../models'
import type {AppStorage} from '../index'
import type {RestResponse} from './request'
import {QueryBuilder} from '../utils/QueryBuilder'
import {GoogleApiHelper} from '../helpers/GoogleApiHelper'

interface Result {
  formatted_address: string
  geometry: {location: {lng: number; lat: number}}
}

const mapRestPredictionsToAppPredictions = (
  predictions: Array<any>,
): PredictionsModel => {
  return predictions.map(({description, place_id}) => ({
    description,
    placeId: place_id,
  }))
}

export type StaticMapPoint =
  | {type: 'marker'; color: string}
  | {type: 'circle'; color: string; radius: number}
  | {type: 'none'}

export class GoogleApi {
  constructor(
    private storage: AppStorage,
    private platform: 'ios' | 'android' | 'web',
  ) {}

  private baseEndpoint = 'https://maps.googleapis.com/maps/api'

  get googleApiKey(): string {
    switch (this.platform) {
      case 'android':
        return 'AIzaSyDTVYlIapV6NcjhdqcE7RoQYlyPChIonKQ'
      case 'ios':
        return 'AIzaSyDTVYlIapV6NcjhdqcE7RoQYlyPChIonKQ'
      case 'web':
        return 'AIzaSyBZw1xPNRpZ4LQ8TVRnpH3DHr-8Zx2W80U'
    }
  }

  getGoogleStaticMapLink = (params: {
    location: LocationModel
    markerColor?: string
    width?: number
    height?: number
    zoom?: number
  }): string => {
    const color = params.markerColor ?? 'red'
    const lat = params.location.latitude
    const lng = params.location.longitude
    const queryBuilder = new QueryBuilder()
      .param('key', this.googleApiKey)
      .param('size', `${params.width ?? 300}x${params.height ?? 300}`)
      .param('sensor', 'true')
      .param('zoom', params.zoom)
      .param('markers', `color:${color}|${lat},${lng}`)
    return `https://maps.google.com/maps/api/staticmap?${queryBuilder.build()}`
  }

  getGoogleStaticMapLink2 = (params: {
    location: LocationModel
    point?: StaticMapPoint
    width?: number
    height?: number
    zoom?: number
  }): string => {
    const point = params.point ?? {type: 'marker', color: 'red'}
    const lat = params.location.latitude
    const lng = params.location.longitude
    const queryBuilder = new QueryBuilder()
      .param('key', this.googleApiKey)
      .param('size', `${params.width ?? 300}x${params.height ?? 300}`)
      .param('sensor', 'true')
      .param('zoom', params.zoom)
    switch (point.type) {
      case 'marker':
        queryBuilder.param('markers', `color:${point.color}|${lat},${lng}`)
        break
      case 'circle':
        const radius = point.radius
        const encodedPath = GoogleApiHelper.drawCirclePath(lat, lng, radius)
        queryBuilder.param(
          'path',
          `fillcolor:${point.color}|color:${point.color}|enc:${encodedPath}`,
        )
        break
    }
    return `https://maps.google.com/maps/api/staticmap?${queryBuilder.build()}`
  }

  requestPlaceInfo = async (url: string): Promise<PlaceInfoModel | null> => {
    try {
      console.log('[GoogleApi.requestPlaceInfo]', url)
      const response: AxiosResponse<{
        results?: Array<Result>
        result?: Result
      }> = await axios.get(url, {timeout: 10000})
      const data = response.data.result || response.data.results?.[0]
      if (!data) return null

      return {
        address: data.formatted_address,
        location: {
          longitude: data.geometry.location.lng,
          latitude: data.geometry.location.lat,
        },
      }
    } catch {
      return null
    }
  }

  fetchPlaceInfo = async (placeId: string | undefined) => {
    if (!placeId) return null
    const lang = this.storage.language
    const endpoint = `${this.baseEndpoint}/place/details/json`
    const apiUrl = `${endpoint}?place_id=${placeId}&fields=formatted_address,geometry&key=${this.googleApiKey}&language=${lang}`
    return await this.requestPlaceInfo(apiUrl)
  }

  fetchGeocode = async (
    location: LocationModel | null,
  ): Promise<PlaceInfoModel | null> => {
    if (!location) return null
    const lang = this.storage.language
    const {latitude, longitude} = location

    const endpoint = `${this.baseEndpoint}/geocode/json`
    const apiUrl = `${endpoint}?latlng=${latitude},${longitude}&key=${this.googleApiKey}&language=${lang}`
    return await this.requestPlaceInfo(apiUrl)
  }

  fetchAutocompletePredictions = async (
    destination: string,
  ): Promise<RestResponse<PredictionsModel>> => {
    if (!destination) return {result: []}
    const location = this.storage.location
    const lang = this.storage.language

    const endpoint = `${this.baseEndpoint}/place/autocomplete/json`
    const apiUrl = `${endpoint}?key=${this.googleApiKey}&input=${destination}&language=${lang}`

    if (location)
      apiUrl.concat(`&location=${location.latitude}, ${location.longitude}`)

    try {
      const response: AxiosResponse<any> = await axios.get(encodeURI(apiUrl), {
        timeout: 5000,
      })
      return {
        result: mapRestPredictionsToAppPredictions(response.data.predictions),
      }
    } catch (e) {
      return {error: (e as Error).message}
    }
  }
}
